import { fetchSportBetsHistory } from "@shared/actions/user";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { Coupon, TCoupon } from "./Coupon";

interface ISportBetsHistoryListProps {
  status: "created" | "non-created";
}

export function SportBetsHistoryList({ status }: ISportBetsHistoryListProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const betHistory = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ history }: Record<string, any>) =>
      history.sportBetsHistory[status].items as TCoupon[]
  );
  const isBetHistoryExist = betHistory.length > 0;

  const isLoading = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ history }: Record<string, any>) =>
      history.sportBetsHistory[status].isPending as boolean
  );
  const hasNextPage = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ history }: Record<string, any>) =>
      history.sportBetsHistory[status].hasNextPage as boolean
  );
  const isFetchingNextPage = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ history }: Record<string, any>) =>
      history.sportBetsHistory[status].isFetchingNextPage as boolean
  );

  useEffect(() => {
    console.debug("fetchSportBetsHistory hasNextPage", hasNextPage);
    if (hasNextPage) {
      dispatch(
        fetchSportBetsHistory({
          status,
          limit: 100,
          offset: betHistory.length,
        })
      );
    }
  }, [betHistory, hasNextPage, dispatch, status]);

  return (
    <div
      className="overflow-y-auto relative min-h-0 h-full max-h-full scrollbar-thin"
      style={{
        scrollbarColor: "var(--theme-color) #f6f7f9",
      }}
    >
      {!isBetHistoryExist && !isFetchingNextPage && !isLoading && (
        <div className="flex-center">
          <div className="text-[1em] text-theme-color font-bold text-center">
            <div>
              {status === "created"
                ? intl.formatMessage({
                    id: "boilerplate.components.SportBetsHistoryModal.emptyActiveBets",
                    defaultMessage: "You have no active bets",
                  })
                : intl.formatMessage({
                    id: "boilerplate.components.SportBetsHistoryModal.emptyCompletedBets",
                    defaultMessage: "You have no COMPLETED bets",
                  })}
            </div>
            <div className="my-3 h-20">
              <svg
                className="block"
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 325 200"
              >
                {/* eslint-disable react/no-unknown-property */}
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="M38.343 0h248.215c21.143 0 38.343 17.203 38.343 38.346v47.79l-12.501.59c-7.112.337-12.683 6.169-12.683 13.276s5.571 12.94 12.678 13.272l12.506.585v47.8c0 21.138-17.2 38.341-38.343 38.341H38.343C17.199 200 0 182.797 0 161.659v-46.893l11.259-1.61c6.502-.933 11.406-6.59 11.406-13.154 0-6.57-4.906-12.227-11.412-13.16L0 85.228V38.345C0 17.203 17.2 0 38.343 0m248.256 100.002c0-14.179 11.168-25.718 25.183-26.38V38.345c0-13.932-11.294-25.227-25.224-25.227H38.343c-13.93 0-25.225 11.295-25.225 25.227v35.512c12.809 1.84 22.665 12.827 22.665 26.145s-9.856 24.305-22.665 26.14v35.516c0 13.929 11.294 25.223 25.225 25.223h248.215c13.93 0 25.224-11.294 25.224-25.223v-35.28c-14.015-.657-25.183-12.196-25.183-26.376m-211.277 48.33h8.679v22.635h-8.679zm8.679-39.766h-8.679v22.635h8.679zm-8.679-39.773h8.679v22.639h-8.679zm8.679-39.758h-8.679V51.67h8.679zm96.305 79.938c-9.913 9.913-25.984 9.913-35.897 0s-9.913-25.984 0-35.897 25.984-9.912 35.897 0 9.912 25.985 0 35.897m4.113 13.091c-14.869 10.595-35.645 9.223-48.984-4.116-14.869-14.869-14.869-38.977 0-53.846s38.976-14.869 53.845 0c13.184 13.184 14.678 33.63 4.483 48.46l13.913 13.913 2.244-2.245 21.987 21.987-13.91 13.91-21.987-21.987 2.243-2.242z"
                />
              </svg>
            </div>
            {status === "created" && (
              <div>
                {intl.formatMessage({
                  id: "boilerplate.components.SportBetsHistoryModal.placeBid",
                  defaultMessage: "Place a new bid",
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {isLoading && (
        <>
          <div className="rounded-rounded bg-silver mb-5 h-56 animate-pulse" />
          <div className="rounded-rounded bg-silver mb-5 h-56 animate-pulse" />
        </>
      )}

      {isBetHistoryExist &&
        betHistory.map((item, idx) => <Coupon key={idx} {...item} />)}

      {isFetchingNextPage && (
        <div className="flex items-center justify-center my-2 h-10">
          <div className="size-5 text-keppel animate-spin">
            <svg
              className="block overflow-visible"
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              fill="none"
              viewBox="0 0 31 31"
            >
              {/* eslint-disable react/no-unknown-property */}
              <path
                fill="currentColor"
                d="M3.44 15.5a12.06 12.06 0 0 1 24.12 0H23.3l5.97 10.33 5.96-10.33H31A15.5 15.5 0 1 0 15.5 31v-3.44c-6.66 0-12.06-5.4-12.06-12.06"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}
